/**
 * `T` may be either a `string` (basic typeahead), an `IBookContract`
 * (editions, dictionaries) or also an IHeadwordList (dictionary headwords).
 */
class SearchBox<T extends string | IBookContract | IHeadwordList>
{
     private inputField: string;
     private urlWithController: string;
     private options: Twitter.Typeahead.Options;
    private datasets: Array<Twitter.Typeahead.Dataset<T>>;
    private bloodhounds: Array<Bloodhound<T>>;

     constructor(inputFieldElement: string, controllerPath: string) {
         this.inputField = inputFieldElement;
         this.urlWithController = getBaseUrl() + controllerPath;
         this.datasets = [];
         this.bloodhounds = [];

         this.options = {
             hint: true,
             highlight: true,
             minLength: 1
         };
     }

     value(value: any):void {
         $(this.inputField).typeahead('val', value);
     }

     create(): void {
         $(this.inputField).typeahead(this.options, this.datasets);
     }

     destroy(): void {
         $(this.inputField).typeahead("destroy");
     }

    // NOTE: this might seem wasteful but the caches really need to be dropped
    //       because changed categories and books really invalidate anything we
    //       already had
     clearAndDestroy(): void {
         for (var i = 0; i < this.bloodhounds.length; i++) {
             var bloodhound = this.bloodhounds[i];
             bloodhound.clear();
             bloodhound.clearPrefetchCache();
             bloodhound.clearRemoteCache();
         }
         this.datasets = [];
         this.bloodhounds = [];
         this.destroy();
     }

    addDataSet(
        name: string,
        groupHeader: string,
        parameterUrlString: string = null
    ): void
    {
         var prefetchUrl: string = this.urlWithController + "/GetTypeahead" + name;
         var remoteUrl: string = this.urlWithController + "/GetTypeahead" + name + "?query=%QUERY";

         if (parameterUrlString != null) {
             prefetchUrl += "?" + parameterUrlString;
             remoteUrl += "&" + parameterUrlString;
         }

        var remoteOptions: Bloodhound.RemoteOptions<T> = {
             url: remoteUrl,
             wildcard: "%QUERY"
         };

        this.setupRemoteOptions(remoteOptions);

        var bloodhound: Bloodhound<T> = new Bloodhound<T>({
            // @ts-ignore
             datumTokenizer: Bloodhound.tokenizers.whitespace,
             queryTokenizer: Bloodhound.tokenizers.whitespace,
             prefetch: prefetchUrl,
             remote: remoteOptions
         });


        const dataset: Twitter.Typeahead.Dataset<T> =
        {
            name: name,
            source: bloodhound,
            templates: {
                header: `<div class="tt-suggestions-header">${groupHeader}</div>`,
            },

            limit: 100,
        };

        this.setupDataset(dataset);

        this.bloodhounds.push(bloodhound);
        this.datasets.push(dataset);
    }

    setupRemoteOptions(remoteOptions: Bloodhound.RemoteOptions<T>)
    {
        // used by the HeadwordsSearchBox
    }

    setupDataset(dataset: Twitter.Typeahead.Dataset<T>)
    {
        // overridden/implemented by BooksSearchBox and HeadwordsSearchBox
    }
}

class BooksSearchBox extends SearchBox<IBookContract>
{
    setupDataset(dataset: Twitter.Typeahead.Dataset<IBookContract>)
    {
        const formatBookInfo = (book: IBookContract, html = false) =>
        {
            let ret = `<span class="name">${book.title}</span>`;

            //  XXX: SearchResultDetailContract contains even a structured list
            //       of authors and GetTitleAutocomplete is able to fetch this list…
            //  XXX: so we could use some sort of a BibliographyLine (or a common
            //       CSL library to render the book information)
            // @ts-ignore
            let authors = book.authorsLabel;
            if (authors)
                ret = `${authors}: ${ret}`;

            // @ts-ignore
            let altTitle = book.alternativeTitle;
            if (altTitle)
                ret += ` / ${altTitle}`;

            let abbr = book.sourcePartAbbreviation ?? book.sourceAbbreviation;
            if (abbr)
                ret += ` [${abbr}]`;

            return html ? ret : stripHtml(ret);
        };

        // https://css-tricks.com/snippets/javascript/strip-html-tags-in-javascript/
        const stripHtml = (dirtyString: string) =>
        {
            const doc = new DOMParser().parseFromString(dirtyString, 'text/html');

            return doc.body.textContent || '';
        };

        // NOTE: we may not use formatBookInfo here as the chosen suggestion
        //       (which the user will then query)
        //       should only contain the value of a single field …
        //       … and titles are ambiguous
        //       (though some books – dictionaries – may not have abbreviations yet)
        // DONE: sourcePartAbbreviation [should be unique]
        const displayFn = (book: IBookContract): string =>
        {
            return (
                book.sourcePartAbbreviation ??
                book.sourceAbbreviation ??
                book.title
            );
        };


        // NOTE: see suggestionTemplate
        // @ts-ignore
        dataset.templates.suggestion = (book) =>
        {
            return $('<div>').html(formatBookInfo(book, true));
        };

        dataset.display = displayFn;
    }
}

class HeadwordsSearchBox extends SearchBox<IHeadwordList>
{
    private abbrRegex: RegExp;

    constructor(inputFieldElement: string, controllerPath: string)
    {
        super(inputFieldElement, controllerPath);

        this.abbrRegex = /\.(.*)/;
    }

    setupRemoteOptions(remoteOptions: Bloodhound.RemoteOptions<IHeadwordList>)
    {
        // @ts-ignore
        remoteOptions.transform = (response: IHeadwordList): IHeadword[] =>
        {
            return response.headwordList;
        };

        // TODO: fix retrieval from the index (in the localStorage) – the
        //       transform is not applied somewhere
        // NOTE: it goes south from: local = this.sorter(this.index.search(query));
    }

    setupDataset(dataset: Twitter.Typeahead.Dataset<IHeadwordList>)
    {
        const formatHeadwordInfo = (headwordsGroup: IHeadword) =>
        {
            let ret = headwordsGroup.headword;

            const dicts = {};

            for (let headword of headwordsGroup.dictionaries) {
                if (headword == null)
                    continue;

                // TODO: get the actual dictionary abbreviation from the
                //       bookList: IBookListDictionary (ideally a shared one)
                const abbr: string = headword.headwordExternalId.replace(
                    this.abbrRegex, '');

                dicts[abbr] = abbr;
            }

            let abbrs = Object.values(dicts).sort();

            abbrs = abbrs.map((abbr) =>
            {
                return `<span class="bold ${abbr}">${abbr}</span>`;
            });

            const cls = 'dictionary-result-book-list';

            if (abbrs) {
                ret += ` <span class="${cls}">${abbrs.join(' | ')}</span>`;
            }

            return ret;
        }

        //  XXX: rather whatever gets returned to the searchbox once the
        //       suggestion is selected/picked/chosen
        // @ts-ignore
        dataset.display = (headword: IHeadword): string =>
        {
            return headword.headword;
        };

        // @ts-ignore
        dataset.templates.suggestion = (headword: IHeadword) =>
        {
            return $('<div>').html(formatHeadwordInfo(headword));
        };
    }
}
